import { loadTheme } from "@fluentui/react"
import { IPartialTheme } from "@fluentui/react/lib/Styling"
import { ITheme, ThemeProvider, mergeThemes } from "@fluentui/react/lib/Theme"
import { Bus } from "baconjs"
import { merge } from "lodash"
import React, {
    ForwardedRef,
    MutableRefObject,
    ReactElement,
    useEffect,
    useRef,
    useState
} from "react"

import { L10n } from "@encoway/l10n"
import { ComponentFactory } from "@encoway/react-configurator"
import {
    OpenIdConnect,
    AuthTokenProvider,
    useToken
} from "@encoway/sales-showroom-auth"

import { extractJSessionId, getSettings, IAppSettings } from "../../appUtil"
import { ComponentName } from "../../constants"
import { IVisualEditor } from "../../services/visualization/visualizationService.types"
import { AppSwitch } from "../AppSwitch/AppSwitch"
import { IThemedApplicationProps } from "./ThemedApplication.types"

const _loadSettings = async (
    theme: ITheme,
    translations: object,
    providedSettings?: Promise<IAppSettings>
) => {
    const _settings = await getSettings(providedSettings, translations)

    if (_settings.theme !== theme) {
        if (_settings.theme) {
            _settings.theme = merge({}, mergeThemes(theme, _settings.theme))
        } else {
            _settings.theme = theme
        }
        loadTheme(_settings.theme)
    }
    return _settings
}

interface ITokenInitializerProps {
    tokenRef: MutableRefObject<AuthTokenProvider>
}
const TokenInitializer: (props: ITokenInitializerProps) => null = (
    props: ITokenInitializerProps
) => {
    const token = useToken()
    if (!props.tokenRef.current && token) {
        props.tokenRef.current = token
    }
    return null
}

const error: (message: string) => React.ReactElement = (message: string) => {
    return ComponentFactory.instanceOf(ComponentName.MessageBox, {
        title: L10n.format("Error"),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onClick: () => {},
        message
    })
}

const Waiter: () => React.ReactElement = () => {
    return ComponentFactory.instanceOf(ComponentName.InitialLoadingSpinner, {
        show: true,
        label: new URLSearchParams(window.location.search).get("product")
    })
}
const IThemedApplication = (
    props: IThemedApplicationProps,
    visualEditorForwardRef: ForwardedRef<IVisualEditor>
): ReactElement | null => {
    const [settings, setSettings] = useState<IAppSettings>()
    const { eventBus, appSettings, appRefineProvider } = props
    const tokenFunction = useRef<AuthTokenProvider>()
    useEffect(() => {
        const providedSettings = appSettings && Promise.resolve(appSettings)
        _loadSettings(props.theme, props.translations, providedSettings)
            .then((s) => {
                const merged = merge({}, s)
                if (s.configuration?.token) {
                    tokenFunction.current = s.configuration.token
                }
                let configuration = {
                    jsessionid: extractJSessionId(window.location.href)
                }
                if (
                    tokenFunction.current ||
                    s.oauth2?.oath2ServerAuthenticationUrl
                ) {
                    configuration = merge(configuration, {
                        token: () =>
                            typeof tokenFunction.current === "function"
                                ? tokenFunction.current()
                                : tokenFunction.current
                    })
                }
                return merge(merged, { configuration })
            })
            .then(setSettings)
    }, [props, appSettings])
    if (!settings) {
        return Waiter()
    }
    return (
        <OpenIdConnect
            realm={settings.oauth2?.realm || ""}
            clientSecret={settings.oauth2?.clientSecret}
            authenticationServerUrl={
                settings.oauth2?.oath2ServerAuthenticationUrl
            }
            clientId={settings.oauth2?.applicationId || ""}
            redirectUri={settings.oauth2?.redirectUri}
            notifyWaiting={Waiter}
            notifyError={error}
            skip={
                settings.oauth2 === undefined ||
                settings.oauth2?.oath2ServerAuthenticationUrl === undefined ||
                settings.configuration.jsessionid !== undefined ||
                tokenFunction.current !== undefined
            }
        >
            <TokenInitializer tokenRef={tokenFunction} />
            <ThemeProvider
                theme={settings.theme as IPartialTheme}
                style={{ display: "flex", flex: 1, minWidth: 0 }}
            >
                <AppSwitch
                    settings={settings}
                    eventBus={eventBus || new Bus()}
                    ref={visualEditorForwardRef}
                    appRefineProvider={appRefineProvider}
                />
            </ThemeProvider>
        </OpenIdConnect>
    )
}
export const ThemedApplication = React.forwardRef(
    (
        props: IThemedApplicationProps,
        visualEditorForwardRef: ForwardedRef<IVisualEditor>
    ) => IThemedApplication(props, visualEditorForwardRef)
)
ThemedApplication.displayName = "ThemedApplication"
